/* NicknameEditor — small collapsible card that lets a claimed Explorer
 * change their nickname (subject to the 90-day cooldown enforced by the
 * backend). It reuses the same real-time availability check as the
 * onboarding modal in SubscriptionLayer.
 */
import { useEffect, useRef, useState } from 'react';
import { Sparkles, Loader2, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function NicknameEditor({ currentNickname, onChanged }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [check, setCheck] = useState({ available: null, valid: null, reason: null, suggestions: [] });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounce = useRef();

  useEffect(() => {
    if (!open) return;
    setValue(currentNickname || '');
    setCheck({ available: null, valid: null, reason: null, suggestions: [] });
  }, [open, currentNickname]);

  useEffect(() => {
    if (!open) return;
    if (!value || value === currentNickname) {
      setCheck({ available: null, valid: null, reason: null, suggestions: [] });
      return;
    }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/nicknames/check?value=${encodeURIComponent(value)}`);
        setCheck(data);
      } catch (_e) { /* silent */ }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(debounce.current);
  }, [value, open, currentNickname]);

  const canSave = value.length >= 3 && value !== currentNickname
                  && check.available === true && check.valid !== false;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/nicknames/claim', { value });
      toast.success(`Nickname changed to ${data.nickname}`);
      onChanged?.(data.nickname);
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not change nickname';
      // Cooldown responses come back as 429 with human-readable text.
      toast.error(String(detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl glass border border-white/10 p-4" data-testid="nickname-editor">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-accent/15 border border-accent/40 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Explorer Nickname</p>
          <p className="font-serif text-lg truncate" data-testid="nickname-current">{currentNickname || '—'}</p>
        </div>
        <button
          onClick={() => setOpen(v => !v)}
          data-testid="nickname-edit-toggle"
          className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5">
          <Edit3 className="w-3 h-3" /> {open ? 'Cancel' : 'Change'}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-2" data-testid="nickname-editor-panel">
          <p className="text-[10px] text-white/60">
            One change every 90 days · 3–20 characters · letters, numbers, underscores.
          </p>
          <div className="relative">
            <input
              value={value}
              onChange={e => setValue(e.target.value)}
              maxLength={20}
              data-testid="nickname-editor-input"
              className="w-full h-11 rounded-xl bg-black/50 border border-white/15 px-4 pr-24 text-sm outline-none focus:border-accent/60"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest">
              {busy ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> :
               value === currentNickname ? <span className="text-muted-foreground">Unchanged</span> :
               check.available === true ? <span className="text-emerald-400">Available</span> :
               check.available === false ? <span className="text-red-400">Taken</span> :
               check.valid === false ? <span className="text-red-400">Invalid</span> : null}
            </span>
          </div>
          {check.reason && (
            <p className="text-xs text-red-300" data-testid="nickname-editor-reason">{check.reason}</p>
          )}
          {check.suggestions?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {check.suggestions.map((s, i) => (
                <button key={i} onClick={() => setValue(s)}
                  className="text-xs px-2.5 py-1 rounded-full bg-accent/10 border border-accent/40 text-accent hover:bg-accent/20"
                  data-testid="nickname-editor-suggestion">{s}</button>
              ))}
            </div>
          )}
          <button
            onClick={save}
            disabled={!canSave || saving}
            data-testid="nickname-editor-save"
            className="w-full h-10 rounded-full bg-accent text-black text-[11px] uppercase tracking-widest disabled:opacity-40">
            {saving ? 'Saving…' : 'Save new nickname'}
          </button>
        </div>
      )}
    </div>
  );
}
